import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { auth, db } from "../../../config/firebaseConfig";
import envConfig from "../../../config/envConfig";
import { AI_LAB_COURSES } from "../constants/ai-lab-constants";
import { updateUserDetails } from "../../../redux/features/authSlice";
import { fetchParentsStudents } from "../../../redux/features/parentStudentsSlice";

// Utilities
import {
  getNextClassDateTime,
  isClassActive,
  getJoinLink,
} from "./utils/classScheduleUtils";

// Dashboard Components & Modals
import PractidoAppBanner from "./components/PractidoAppBanner";
import DashboardWelcomeSection from "./components/DashboardWelcomeSection";
import MyChildrenSection from "./components/MyChildrenSection";
import UpcomingClassesEventsSection from "./components/UpcomingClassesEventsSection";
import LiveProgramsSection from "./components/LiveProgramsSection";
import QuickActionsSection from "./components/QuickActionsSection";
import PractidoAppPaymentModule from "./components/PractidoAppPaymentModule";
import DashboardInstructions from "./components/DashboardInstructions";
import AddChildModal from "./components/AddChildModal";
import BookDemoModal from "./components/BookDemoModal";
import VerifyPhoneModal from "./components/VerifyPhoneModal";
import ClassGateModal from "./components/ClassGateModal";
import StudentAuthModal from "./components/StudentAuthModal";
import ChildSelectModal from "./components/ChildSelectModal";
import SelectWorkspaceModal from "./components/SelectWorkspaceModal";
import SchoolLogo from "../school/SchoolLogo";

const ParentDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const isBookDemoQuery = searchParams.get("action") === "book-demo";
  const isAiLabOffer = searchParams.get("offer") === "ai-lab";

  // Redux auth details
  const {
    userDetails: parentsDetails,
    loading: parentDetailsLoading,
    schoolDetails,
  } = useSelector((state) => state.auth);

  // Dashboard state
  const [dashboardChildren, setDashboardChildren] = useState([]);
  const [childrenLoading, setChildrenLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [activeEnrollments, setActiveEnrollments] = useState([]);
  const [pendingEnrollments, setPendingEnrollments] = useState([]);

  // Modals state
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [childToEdit, setChildToEdit] = useState(null);
  const [showBookDemoModal, setShowBookDemoModal] = useState(isBookDemoQuery);
  const [showChildSelectModal, setShowChildSelectModal] = useState(false);
  const [showSelectWorkspaceModal, setShowSelectWorkspaceModal] = useState(false);
  const [childForWorkspaceSelect, setChildForWorkspaceSelect] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(
    () => sessionStorage.getItem("open_verify_phone_modal_on_load") === "true"
  );

  // Student Class Gate State
  const [selectedGrade, setSelectedGrade] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const isChild = parentsDetails?.accountType === "student";
  const showClassGate = isChild && !parentsDetails?.grade;

  // Student Authentication / Linking State
  const [authenticatingStudent, setAuthenticatingStudent] = useState(null);
  const [studentAuthMethod, setStudentAuthMethod] = useState("otp"); // "otp" | "google"
  const [studentPhone, setStudentPhone] = useState("");
  const [studentEmail, setStudentEmail] = useState("");
  const [studentOtp, setStudentOtp] = useState("");
  const [studentOtpSent, setStudentOtpSent] = useState(false);
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentError, setStudentError] = useState("");
  const [studentSuccess, setStudentSuccess] = useState(false);
  const [studentTimer, setStudentTimer] = useState(0);

  // Email & WhatsApp verification banner state
  const [toastDismissed, setToastDismissed] = useState(
    sessionStorage.getItem("whatsapp_verify_dismissed") === "true"
  );
  const [emailBannerDismissed, setEmailBannerDismissed] = useState(
    sessionStorage.getItem("email_verify_dismissed") === "true"
  );
  const [emailResendLoading, setEmailResendLoading] = useState(false);
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(
    () => auth.currentUser?.emailVerified || parentsDetails?.emailVerified || false
  );

  useEffect(() => {
    if (sessionStorage.getItem("open_verify_phone_modal_on_load") === "true") {
      sessionStorage.removeItem("open_verify_phone_modal_on_load");
    }
  }, []);

  // Timer cooldown for email verification resend
  useEffect(() => {
    let interval = null;
    if (emailResendCooldown > 0) {
      interval = setInterval(() => {
        setEmailResendCooldown((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [emailResendCooldown]);

  // Sync auth.currentUser emailVerified status on focus and reload
  useEffect(() => {
    const checkEmailStatus = async () => {
      if (auth.currentUser) {
        await auth.currentUser.reload().catch(() => null);
        if (auth.currentUser.emailVerified) {
          setIsEmailVerified(true);
        }
      }
    };
    checkEmailStatus();
    window.addEventListener("focus", checkEmailStatus);
    return () => window.removeEventListener("focus", checkEmailStatus);
  }, []);

  const handleSendEmailVerification = async () => {
    if (!auth.currentUser) return;
    setEmailResendLoading(true);
    try {
      const { sendEmailVerification } = await import("firebase/auth");
      await sendEmailVerification(auth.currentUser);
      setEmailSentSuccess(true);
      setEmailResendCooldown(60);
      toast.success("Verification link sent to your email!");
    } catch (err) {
      console.error("Error sending verification email:", err);
      toast.error(err?.message || "Failed to send verification email.");
    } finally {
      setEmailResendLoading(false);
    }
  };

  // Fetch batches
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const snap = await getDocs(collection(db, "ai-lab-batches"));
        setBatches(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Failed to fetch batches:", err);
      }
    };
    fetchBatches();
  }, []);

  // Fetch enrollments
  const fetchEnrollments = useCallback(async () => {
    const parentId = parentsDetails?.id || auth?.currentUser?.uid;
    if (!parentId) {
      setChildrenLoading(false);
      return;
    }

    if (parentsDetails?.accountType === "student") {
      const aiLab = parentsDetails?.enrolment?.["ai-lab"];
      if (aiLab) {
        if (aiLab.paymentStatus === "verified") {
          setActiveEnrollments([{ id: parentId, ...aiLab }]);
          setPendingEnrollments([]);
        } else if (aiLab.paymentStatus === "pending") {
          setPendingEnrollments([{ id: parentId, ...aiLab }]);
          setActiveEnrollments([]);
        }
      } else {
        setActiveEnrollments([]);
        setPendingEnrollments([]);
      }
      setChildrenLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, "students"),
        where("parentUid", "==", parentId)
      );
      const snap = await getDocs(q);
      const activeList = [];
      const pendingList = [];
      const childrenList = [];
      snap.docs.forEach((d) => {
        const childData = d.data();
        childrenList.push({ id: d.id, ...childData });
        const aiLab = childData.enrolment?.["ai-lab"];
        if (aiLab) {
          if (aiLab.paymentStatus === "verified") {
            activeList.push({ id: d.id, ...aiLab, childName: childData.name });
          } else if (aiLab.paymentStatus === "pending") {
            pendingList.push({ id: d.id, ...aiLab, childName: childData.name });
          }
        }
      });
      setActiveEnrollments(activeList);
      setPendingEnrollments(pendingList);
      setDashboardChildren(childrenList);
    } catch (err) {
      console.error("Error fetching AI Lab enrollments:", err);
    } finally {
      setChildrenLoading(false);
    }
  }, [parentsDetails]);

  useEffect(() => {
    fetchEnrollments();
  }, [fetchEnrollments]);

  useEffect(() => {
    if (isAiLabOffer) {
      navigate("/dashboard/ai-lab/enrol", { replace: true });
    }
  }, [isAiLabOffer, navigate]);

  // Children handlers
  const handleChildAddedFromDashboard = (newChild) => {
    setDashboardChildren((prev) => [...prev, newChild]);
    const parentId = parentsDetails?.id || parentsDetails?.uid || auth?.currentUser?.uid;
    if (parentId) {
      dispatch(fetchParentsStudents({ parentUid: parentId }));
    }
    const childId = newChild.id || newChild.uid;
    navigate(`/dashboard/code-workspace${childId ? `?childId=${childId}` : ""}`);
  };

  const handleChildUpdatedFromDashboard = (updatedChild) => {
    const updatedId = updatedChild.id || updatedChild.uid;
    setDashboardChildren((prev) =>
      prev.map((c) => ((c.id || c.uid) === updatedId ? updatedChild : c))
    );
    const parentId = parentsDetails?.id || parentsDetails?.uid || auth?.currentUser?.uid;
    if (parentId) {
      dispatch(fetchParentsStudents({ parentUid: parentId }));
    }
  };

  const handleEditChild = (child) => {
    setChildToEdit(child);
    setShowAddChildModal(true);
  };

  const handleRemoveChild = async (student) => {
    if (!student?.id) return;
    const confirmDelete = window.confirm(
      `Are you sure you want to remove ${student.name}? This action cannot be undone.`
    );
    if (confirmDelete) {
      try {
        await deleteDoc(doc(db, "students", student.id));
        toast.success(`${student.name} removed successfully.`);
        fetchEnrollments();
      } catch (err) {
        console.error("Error removing student:", err);
        toast.error("Failed to remove student. Please try again.");
      }
    }
  };

  const handleLaunchWorkspaceForChild = (child) => {
    if (!child) return;
    const childId = child.id || child.uid;
    if (!child.workspace) {
      setChildForWorkspaceSelect(child);
      setShowSelectWorkspaceModal(true);
    } else {
      navigate(`/dashboard/code-workspace${childId ? `?childId=${childId}` : ""}`);
    }
  };

  const handleWorkspaceSet = (updatedChild) => {
    const updatedId = updatedChild.id || updatedChild.uid;
    setDashboardChildren((prev) =>
      prev.map((c) => ((c.id || c.uid) === updatedId ? updatedChild : c))
    );
    const parentId = parentsDetails?.id || parentsDetails?.uid || auth?.currentUser?.uid;
    if (parentId) {
      dispatch(fetchParentsStudents({ parentUid: parentId }));
    }
    navigate(`/dashboard/code-workspace${updatedId ? `?childId=${updatedId}` : ""}`);
  };

  const handleOpenCodeWorkspace = () => {
    if (parentsDetails?.accountType === "student") {
      navigate(`/dashboard/code-workspace`);
    } else {
      setShowChildSelectModal(true);
    }
  };

  // Student auth OTP timer
  useEffect(() => {
    let interval = null;
    if (studentTimer > 0) {
      interval = setInterval(() => {
        setStudentTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [studentTimer]);

  const handleSendStudentOtp = async (e) => {
    e?.preventDefault();
    if (!studentPhone || studentPhone.length !== 10) {
      setStudentError("Please enter a valid 10-digit phone number.");
      return;
    }
    setStudentLoading(true);
    setStudentError("");
    try {
      const response = await fetch(`${envConfig.backendUrl}/sendWhatsappOtpV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: studentPhone, purpose: "Registration" }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to send OTP.");
      }
      setStudentOtpSent(true);
      setStudentTimer(60);
    } catch (err) {
      setStudentError(err.message || "Failed to send OTP. Please try again.");
    } finally {
      setStudentLoading(false);
    }
  };

  const handleVerifyStudentOtp = async (e) => {
    e?.preventDefault();
    if (!studentOtp || studentOtp.length !== 6) {
      setStudentError("Please enter the 6-digit OTP code.");
      return;
    }
    setStudentLoading(true);
    setStudentError("");
    try {
      const response = await fetch(`${envConfig.backendUrl}/authenticateStudentWebV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          studentDocId: authenticatingStudent.id,
          phoneNumber: studentPhone,
          otp: studentOtp,
          email: studentEmail,
          name: authenticatingStudent.name,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Authentication failed.");
      }
      setStudentSuccess(true);
      setDashboardChildren((prev) =>
        prev.map((c) =>
          c.id === authenticatingStudent.id
            ? { ...c, isAuthSetup: true, phoneNumber: studentPhone, phone: studentPhone, email: studentEmail }
            : c
        )
      );
      setTimeout(() => {
        setAuthenticatingStudent(null);
        setStudentPhone("");
        setStudentEmail("");
        setStudentOtp("");
        setStudentOtpSent(false);
        setStudentSuccess(false);
      }, 1500);
    } catch (err) {
      setStudentError(err.message || "Failed to verify student. Please try again.");
    } finally {
      setStudentLoading(false);
    }
  };

  const handleGoogleLinkStudent = async () => {
    setStudentLoading(true);
    setStudentError("");
    let tempApp = null;
    try {
      const { initializeApp } = await import("firebase/app");
      const { getAuth, signInWithPopup, GoogleAuthProvider } = await import("firebase/auth");

      const isProd = process.env.REACT_APP_URL === "https://practido.com/";
      const resolveEnvKey = (key) => {
        return (
          process.env[`REACT_APP_FIREBASE_${key}`] ||
          process.env[`REACT_APP_FIREBASE_${isProd ? "MOBILE" : "WEB"}_${key}`]
        );
      };

      tempApp = initializeApp(
        {
          apiKey: resolveEnvKey("API_KEY"),
          authDomain: resolveEnvKey("AUTH_DOMAIN"),
          projectId: resolveEnvKey("PROJECT_ID"),
          storageBucket: resolveEnvKey("STORAGE_BUCKET"),
          messagingSenderId: resolveEnvKey("MESSAGING_SENDER_ID"),
          appId: resolveEnvKey("APP_ID"),
          measurementId: resolveEnvKey("MEASUREMENT_ID"),
        },
        "tempChildAuth"
      );

      const tempAuth = getAuth(tempApp);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });

      const result = await signInWithPopup(tempAuth, provider);
      const childUser = result.user;

      if (!childUser.email) {
        throw new Error("Could not retrieve email from Google account.");
      }

      const response = await fetch(`${envConfig.backendUrl}/linkStudentGoogleWebV1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          studentDocId: authenticatingStudent.id,
          email: childUser.email,
          name: childUser.displayName || authenticatingStudent.name,
          uid: childUser.uid,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to link Google account.");
      }

      setStudentSuccess(true);
      setDashboardChildren((prev) =>
        prev.map((c) =>
          c.id === authenticatingStudent.id
            ? {
                ...c,
                id: childUser.uid,
                isAuthSetup: true,
                email: childUser.email,
                authProvider: "google",
                phoneNumber: "",
                phone: "",
              }
            : c
        )
      );

      setTimeout(() => {
        setAuthenticatingStudent(null);
        setStudentSuccess(false);
      }, 1500);
    } catch (err) {
      setStudentError(err.message || "Google linking failed.");
    } finally {
      if (tempApp) {
        try {
          await tempApp.delete();
        } catch (e) {
          console.error("Error deleting temp app:", e);
        }
      }
      setStudentLoading(false);
    }
  };

  const handleSaveGrade = async (e) => {
    e.preventDefault();
    if (!selectedGrade) {
      setModalError("Please select a class");
      return;
    }
    setModalLoading(true);
    setModalError("");
    try {
      const uid = auth?.currentUser?.uid || parentsDetails?.uid;
      if (uid) {
        await updateDoc(doc(db, "students", uid), {
          grade: selectedGrade,
        });
        dispatch(updateUserDetails({ grade: selectedGrade }));
      }
    } catch (err) {
      console.error("Error saving grade:", err);
      setModalError("Failed to update class. Please try again.");
    } finally {
      setModalLoading(false);
    }
  };

  // Enrolled upcoming classes calculation
  const getEnrolledClasses = () => {
    const list = [];
    const childrenToScan =
      dashboardChildren.length > 0
        ? dashboardChildren
        : parentsDetails?.accountType === "student" && parentsDetails
        ? [parentsDetails]
        : [];

    childrenToScan.forEach((child) => {
      const aiLab = child.enrolment?.["ai-lab"];
      if (aiLab && aiLab.paymentStatus === "verified" && aiLab.courses) {
        Object.entries(aiLab.courses).forEach(([courseId, sched]) => {
          if (sched && sched.day && sched.timeSlot) {
            const courseInfo = AI_LAB_COURSES.find((c) => c.id === courseId);
            const nextDate = getNextClassDateTime(sched.day, sched.timeSlot);
            list.push({
              childId: child.id || child.uid,
              childName: child.name || "Student",
              childGrade: child.grade || child.studentClass || "Class N/A",
              courseId,
              courseName: courseInfo?.label || "AI & Robotics",
              courseInfo,
              day: sched.day,
              timeSlot: sched.timeSlot,
              nextDate,
              active: isClassActive(sched.day, sched.timeSlot),
              meetLink: getJoinLink(child.name || "Student", courseId, sched.day, sched.timeSlot, batches),
            });
          }
        });
      }
    });

    return list
      .filter((item) => item.nextDate !== null)
      .sort((a, b) => a.nextDate.getTime() - b.nextDate.getTime());
  };

  const upcomingClasses = getEnrolledClasses();

  return (
    <div className="parent-dashboard pt-6 sm:pt-8 space-y-6 text-left max-w-7xl mx-auto pb-12">
      {/* Task 2: Explore Practido App Banner */}
      <PractidoAppBanner />

      {/* Task 3: Welcome Section & Verification Banners */}
      <DashboardWelcomeSection
        accountType={parentsDetails?.accountType}
        showWhatsappBanner={!parentsDetails?.isMobileVerified}
        onVerifyWhatsapp={() => setShowVerifyModal(true)}
        showEmailBanner={!isEmailVerified}
        emailToVerify={parentsDetails?.email || auth.currentUser?.email}
        onSendEmailVerification={handleSendEmailVerification}
        emailResendLoading={emailResendLoading}
        emailResendCooldown={emailResendCooldown}
        emailSentSuccess={emailSentSuccess}
      />

      {/* Child Class Gate Modal */}
      <ClassGateModal
        isOpen={showClassGate}
        selectedGrade={selectedGrade}
        setSelectedGrade={setSelectedGrade}
        modalLoading={modalLoading}
        modalError={modalError}
        onSaveGrade={handleSaveGrade}
      />

      <div className="parents-dashbaord-content space-y-4">
        {parentDetailsLoading ? (
          <div className="col-span-2 flex items-center justify-center gap-5">
            <div className="bg-gray-400 w-20 h-20 overflow-hidden flex items-center justify-center rounded-full flex-shrink-0">
              <div className="w-full h-full bg-gray-400 animate-pulse flex items-center justify-center"></div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="w-48 h-8 bg-gray-300 rounded-lg animate-pulse" />
              <div className="w-32 h-4 bg-gray-300 rounded-lg animate-pulse" />
              <div className="w-40 h-4 bg-gray-300 rounded-lg animate-pulse" />
            </div>
          </div>
        ) : (
          parentsDetails?.schoolId && (
            <div>
              <SchoolLogo schoolDetails={schoolDetails} />
            </div>
          )
        )}

        <div className="space-y-6">
          {/* Pending Enrollment Alert */}
          {pendingEnrollments.length > 0 && (
            <div className="p-4 rounded-2xl border border-amber-200 bg-amber-50 text-left flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-3xs">
              <div className="space-y-1">
                <h4 className="font-extrabold text-amber-900 text-sm flex items-center gap-1.5">
                  <span className="animate-pulse">⚠️</span> Pending Enrolment Found
                </h4>
                <p className="text-xs text-amber-700 font-semibold">
                  You have a pending enrolment. Complete the payment checkout to activate your child's courses.
                </p>
              </div>
              <div className="flex-shrink-0">
                <button
                  onClick={() => navigate("/dashboard/ai-lab/enrol")}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-extrabold shadow-sm transition"
                >
                  Resume Checkout &rarr;
                </button>
              </div>
            </div>
          )}

          {/* Task 4: My Children Section */}
          <MyChildrenSection
            childrenList={dashboardChildren}
            loading={childrenLoading}
            batches={batches}
            onAddChild={() => {
              setChildToEdit(null);
              setShowAddChildModal(true);
            }}
            onEditChild={handleEditChild}
            onRemoveChild={handleRemoveChild}
            onBookDemo={() => {
              setShowBookDemoModal(true);
            }}
            onOpenWorkspace={(child) => {
              handleLaunchWorkspaceForChild(child);
            }}
          />

          {/* Task 5: Upcoming Classes & Events Section */}
          <UpcomingClassesEventsSection
            upcomingClasses={upcomingClasses}
            onEnrolClick={() => navigate("/dashboard/ai-lab/enrol")}
          />

          {/* Task 6: Live Programs Section */}
          <LiveProgramsSection
            activeEnrollments={activeEnrollments}
            isChild={isChild}
            onEnrolClick={() => navigate("/dashboard/ai-lab/enrol")}
            onShowDetailsClick={() => navigate("/dashboard/ai-lab")}
            onBookDemoClick={() => setShowBookDemoModal(true)}
          />

          {/* Task 7: Quick Actions Section */}
          <QuickActionsSection
            onAddChild={() => {
              setChildToEdit(null);
              setShowAddChildModal(true);
            }}
            onBookDemo={() => setShowBookDemoModal(true)}
            onMyEnrollments={null}
            onOpenCodeStudio={handleOpenCodeWorkspace}
            onPayments={null}
          />

          <div id="practido-app-payment-module">
            <PractidoAppPaymentModule
              schoolDetails={schoolDetails}
              parentsDetails={parentsDetails}
            />
          </div>
        </div>
      </div>

      {/* Instructions */}
      <DashboardInstructions placement="parent" />

      {/* Verify Phone Modal */}
      {showVerifyModal && parentsDetails && (
        <VerifyPhoneModal
          parentsDetails={parentsDetails}
          onClose={() => setShowVerifyModal(false)}
        />
      )}

      {/* Student Authentication Modal */}
      <StudentAuthModal
        authenticatingStudent={authenticatingStudent}
        onClose={() => setAuthenticatingStudent(null)}
        studentSuccess={studentSuccess}
        studentError={studentError}
        studentAuthMethod={studentAuthMethod}
        setStudentAuthMethod={setStudentAuthMethod}
        studentOtpSent={studentOtpSent}
        setStudentOtpSent={setStudentOtpSent}
        studentEmail={studentEmail}
        setStudentEmail={setStudentEmail}
        studentPhone={studentPhone}
        setStudentPhone={setStudentPhone}
        studentOtp={studentOtp}
        setStudentOtp={setStudentOtp}
        studentLoading={studentLoading}
        studentTimer={studentTimer}
        onSendStudentOtp={handleSendStudentOtp}
        onVerifyStudentOtp={handleVerifyStudentOtp}
        onGoogleLinkStudent={handleGoogleLinkStudent}
      />

      {/* Child Select Modal for Code Workspace */}
      <ChildSelectModal
        isOpen={showChildSelectModal}
        onClose={() => setShowChildSelectModal(false)}
        childrenList={dashboardChildren}
        onSelectChild={(child) => {
          setShowChildSelectModal(false);
          handleLaunchWorkspaceForChild(child);
        }}
        onAddChild={() => {
          setShowChildSelectModal(false);
          setChildToEdit(null);
          setShowAddChildModal(true);
        }}
      />

      {/* Select Primary Workspace Modal */}
      <SelectWorkspaceModal
        isOpen={showSelectWorkspaceModal}
        onClose={() => {
          setShowSelectWorkspaceModal(false);
          setChildForWorkspaceSelect(null);
        }}
        child={childForWorkspaceSelect}
        onWorkspaceSet={handleWorkspaceSet}
      />

      {/* Add Child Modal */}
      <AddChildModal
        isOpen={showAddChildModal}
        onClose={() => {
          setShowAddChildModal(false);
          setChildToEdit(null);
        }}
        onChildAdded={handleChildAddedFromDashboard}
        onChildUpdated={handleChildUpdatedFromDashboard}
        parentsDetails={parentsDetails}
        childToEdit={childToEdit}
      />

      {/* Book Demo Modal */}
      <BookDemoModal
        isOpen={showBookDemoModal}
        onClose={() => {
          setShowBookDemoModal(false);
          navigate("/dashboard", { replace: true });
        }}
        existingChildren={dashboardChildren}
        parentsDetails={parentsDetails}
        onSuccess={() => {
          setShowBookDemoModal(false);
          fetchEnrollments();
          navigate("/dashboard", { replace: true });
        }}
      />
    </div>
  );
};

export default ParentDashboard;
